export type Identifiable = { id: string };

export const prependToList = <T extends Identifiable>(
  old: T[],
  item: T,
): T[] => {
  if (old.some((row) => row.id === item.id)) {
    return old.map((row) => (row.id === item.id ? item : row));
  }
  return [item, ...old];
};

export const replaceInList = <T extends Identifiable>(
  old: T[],
  item: T,
): T[] => old.map((row) => (row.id === item.id ? item : row));

export const removeFromList = <T extends Identifiable>(
  old: T[],
  id: string,
): T[] => old.filter((row) => row.id !== id);

type ListQueryUtils<TItem> = {
  getData: (input?: undefined) => TItem[] | undefined;
  setData: (
    input: undefined,
    updater: (old: TItem[] | undefined) => TItem[] | undefined,
  ) => unknown;
  invalidate: (input?: undefined) => unknown;
};

export const patchListCache = <T>(
  cache: ListQueryUtils<T>,
  updater: (old: T[]) => T[],
) => {
  const current = cache.getData();
  if (!current) {
    void cache.invalidate();
    return;
  }
  cache.setData(undefined, () => updater(current));
};

type KeyedListQueryUtils<TItem, TInput> = {
  getData: (input: TInput) => TItem[] | undefined;
  setData: (
    input: TInput,
    updater: (old: TItem[] | undefined) => TItem[] | undefined,
  ) => unknown;
  invalidate: (input?: TInput) => unknown;
};

export const patchKeyedListCache = <T, TInput>(
  cache: KeyedListQueryUtils<T, TInput>,
  input: TInput,
  updater: (old: T[]) => T[],
) => {
  const current = cache.getData(input);
  if (!current) {
    void cache.invalidate(input);
    return;
  }
  cache.setData(input, () => updater(current));
};

export const upsertInListBy = <T>(
  old: T[],
  item: T,
  key: keyof T,
): T[] => {
  if (old.some((row) => row[key] === item[key])) {
    return old.map((row) => (row[key] === item[key] ? item : row));
  }
  return [item, ...old];
};

export const removeFromListBy = <T>(
  old: T[],
  key: keyof T,
  value: T[keyof T],
): T[] => old.filter((row) => row[key] !== value);

type QueryUtils<TItem, TInput> = {
  getData: (input: TInput) => TItem | undefined;
  setData: (
    input: TInput,
    updater: (old: TItem | undefined) => TItem | undefined,
  ) => unknown;
  invalidate: (input?: TInput) => unknown;
};

export const patchQueryCache = <T, TInput>(
  cache: QueryUtils<T, TInput>,
  input: TInput,
  updater: (old: T) => T,
) => {
  const current = cache.getData(input);
  if (!current) {
    void cache.invalidate(input);
    return;
  }
  cache.setData(input, () => updater(current));
};
